/**
 * Page Range Parser Utility
 *
 * Utility for parsing page range strings into arrays of page numbers.
 * Supports various formats:
 * - Single pages: "1,3,5" → [1, 3, 5]
 * - Ranges: "1-5" → [1, 2, 3, 4, 5]
 * - Mixed: "1-3,5,7-9" → [1, 2, 3, 5, 7, 8, 9]
 * - Empty/whitespace: "" → null (all pages)
 *
 * @example
 * // Parse single pages
 * const pages = parsePageRange("1,3,5");
 * // → [1, 3, 5]
 *
 * @example
 * // Parse ranges
 * const pages = parsePageRange("1-5");
 * // → [1, 2, 3, 4, 5]
 *
 * @example
 * // Parse mixed format
 * const pages = parsePageRange("1-3,5,7-9");
 * // → [1, 2, 3, 5, 7, 8, 9]
 *
 * @example
 * // All pages
 * const pages = parsePageRange("");
 * // → null (indicates all pages should be processed)
 */

/**
 * Parse a page range string into an array of page numbers
 *
 * @param pageStr - The page range string to parse (e.g., "1-5", "1,3,5", "1-3,5,7-9")
 * @returns Array of page numbers, or null if the string is empty (indicating all pages)
 *
 * @throws Error if the format is invalid or contains invalid page numbers
 *
 * Format rules:
 * - Empty string or whitespace returns null (all pages)
 * - Comma-separated values: "1,2,3"
 * - Dash for ranges: "1-5" expands to [1,2,3,4,5]
 * - Can mix both: "1-3,5,7-9"
 * - Page numbers must be positive integers (≥ 1)
 * - Range start must be ≤ range end
 */
export function parsePageRange(pageStr: string): number[] | null {
  // Handle empty or whitespace-only input
  if (!pageStr || pageStr.trim() === '') {
    return null; // Indicates "all pages"
  }

  try {
    const pages: number[] = [];
    const parts = pageStr.split(',').map(p => p.trim());

    for (const part of parts) {
      if (part.includes('-')) {
        // Handle range like "1-5"
        const [startStr, endStr] = part.split('-').map(n => n.trim());
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);

        // Validation
        if (isNaN(start) || isNaN(end)) {
          throw new Error(`Invalid range: "${part}". Start and end must be numbers.`);
        }
        if (start < 1 || end < 1) {
          throw new Error(`Invalid range: "${part}". Page numbers must be ≥ 1.`);
        }
        if (start > end) {
          throw new Error(`Invalid range: "${part}". Start must be ≤ end.`);
        }

        // Expand range
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      } else {
        // Handle single page like "3"
        const page = parseInt(part, 10);

        // Validation
        if (isNaN(page)) {
          throw new Error(`Invalid page number: "${part}". Must be a number.`);
        }
        if (page < 1) {
          throw new Error(`Invalid page number: "${part}". Must be ≥ 1.`);
        }

        pages.push(page);
      }
    }

    // Return null if no pages were parsed (edge case)
    return pages.length > 0 ? pages : null;
  } catch (error) {
    // Re-throw with user-friendly message
    if (error instanceof Error) {
      throw new Error(
        `Formato pagine non valido. ${error.message}\n\nFormati accettati:\n` +
        `- Singole pagine: "1,3,5"\n` +
        `- Range: "1-5"\n` +
        `- Misto: "1-3,5,7-9"`
      );
    }
    throw error;
  }
}

/**
 * Validate a page range string without parsing it
 *
 * @param pageStr - The page range string to validate
 * @returns true if valid, false otherwise
 *
 * @example
 * isValidPageRange("1-5"); // → true
 * isValidPageRange("abc"); // → false
 * isValidPageRange("5-1"); // → false (start > end)
 */
export function isValidPageRange(pageStr: string): boolean {
  try {
    parsePageRange(pageStr);
    return true;
  } catch {
    return false;
  }
}

/**
 * Format a page array back into a string representation
 *
 * @param pages - Array of page numbers to format
 * @returns Formatted string (e.g., [1,2,3,5] → "1-3,5")
 *
 * @example
 * formatPageRange([1, 2, 3, 5, 7, 8, 9]);
 * // → "1-3,5,7-9"
 *
 * @example
 * formatPageRange([1, 3, 5]);
 * // → "1,3,5"
 */
export function formatPageRange(pages: number[]): string {
  if (!pages || pages.length === 0) {
    return '';
  }

  // Sort and remove duplicates
  const sorted = Array.from(new Set(pages)).sort((a, b) => a - b);

  const ranges: string[] = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    const isConsecutive = current === rangeEnd + 1;

    if (isConsecutive) {
      // Extend current range
      rangeEnd = current;
    } else {
      // Close current range and start new one
      if (rangeStart === rangeEnd) {
        ranges.push(`${rangeStart}`);
      } else if (rangeEnd === rangeStart + 1) {
        // Two consecutive pages - better as "1,2" than "1-2"
        ranges.push(`${rangeStart},${rangeEnd}`);
      } else {
        ranges.push(`${rangeStart}-${rangeEnd}`);
      }

      rangeStart = current;
      rangeEnd = current;
    }
  }

  return ranges.join(',');
}
